import { useCallback, useMemo, useEffect } from 'react';
import { useReactFlow } from '@xyflow/react';
import {
  AppNode,
  DetailPageMode,
  DetailPageStage,
  DetailPageData,
  DetailPagePlannerData,
  DetailPageDesignerData,
  NodeStatus,
  SplitPrompt,
} from '@/types';
import { useNodeStore } from '@/stores/nodeStore';

/** 分块序号 ↔ 标题前缀（「第N区块」）：登记校验正查、自愈补录反查共用；与 useDetailPageActions 的 formatBlockLabel 保持一致 */
const BLOCK_LABELS = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十'];

function formatBlockLabelOf(index: number): string {
  return `第${BLOCK_LABELS[index - 1] ?? index}区块`;
}

/** 从节点标题前缀反解分块序号（如「第一区块·把童趣端上餐桌」→ 1），非分块出图节点返回 undefined */
function parseBlockIndexFromTitle(title: string | undefined): number | undefined {
  const t = title ?? '';
  for (let i = 0; i < BLOCK_LABELS.length; i++) {
    if (t.startsWith(`第${BLOCK_LABELS[i]}区块`)) return i + 1;
  }
  return undefined;
}

/** 分块出图节点是否「已出图或生成中/排队」：补录判定用；内联避免与 useDetailPageActions 循环导入 */
function blockNodeActiveOrDone(node: AppNode): boolean {
  const d = node.data;
  if (d.image || (d.images && d.images.length > 0)) return true;
  return node.status === NodeStatus.WORKING || node.status === NodeStatus.QUEUED;
}

/** 分块出图节点竖排堆叠布局参数：与 useDetailPageActions 的同值常量保持一致（内联避免循环导入） */
const BLOCK_NODE_SHORT_EDGE = 420;
const BLOCK_NODE_TITLE_ALLOWANCE = 44;
/** 每竖排最多堆叠的分块数（满 4 个横向开新竖排），与 useDetailPageActions 同值 */
const BLOCK_STACK_PER_COLUMN = 4;
/** 各竖排内部相邻节点的统一小行距 / 竖排之间的列距（画布像素） */
const BLOCK_STACK_GAP_Y = 60;
const BLOCK_STACK_GAP_X = 200;
const DETAIL_PAGE_HEIGHT_FALLBACK = 1040;

/** 分块出图布局版本：4 = 竖排堆叠、每竖排 4 个按序填充、满 4 横向开新竖排（各竖排内部等距竖排）；缺省/旧值由 syncRegistry 一次性迁移重排 */
export const BLOCK_LAYOUT_VERSION = 4;

/** 分块出图节点画布占位高度（卡片高度按画幅比 + 标题悬浮占位）：布局迁移重排占位估算用 */
function blockNodeFootprintHeight(unitType: SplitPrompt['unitType']): number {
  const ratio = ({ S: '9:16', D: '9:21', T: '1:3' } as Record<SplitPrompt['unitType'], string>)[unitType] ?? '3:4';
  const [w, h] = ratio.split(':').map(Number);
  return Math.round((BLOCK_NODE_SHORT_EDGE * h) / w) + BLOCK_NODE_TITLE_ALLOWANCE;
}

/** 默认风格：空 = 智能推荐（由策划师按产品特点主推，确认后回写实际风格名） */
export const DEFAULT_STYLE = '';

export function defaultPlannerData(): DetailPagePlannerData {
  return {
    productImageCount: 0,
    productImageNodeIds: [],
    sellingPoints: [],
    sellingPointNodeIds: [],
    skipCompetitor: false,
    competitorImageCount: 0,
    competitorImageNodeIds: [],
    connectedProductImageNodeIds: [],
    connectedCompetitorImageNodeIds: [],
    useCompetitorStyleReference: false,
    style: DEFAULT_STYLE,
    model: '',
    imageModel: '',
    advancedOpen: false,
    hasArchitectureTable: false,
    isPlannerComplete: false,
  };
}

export function defaultDesignerData(): DetailPageDesignerData {
  return {
    productName: '',
    architectureSummary: '',
    style: DEFAULT_STYLE,
    model: '',
    hasUpstreamData: false,
    blockLayoutVersion: BLOCK_LAYOUT_VERSION,
  };
}

/** 旧 mode 数据兜底：无 stage 字段时按旧三态 + 完成度推断四阶段 */
export function deriveStage(data: DetailPageData): DetailPageStage {
  if (data.stage) return data.stage;
  switch (data.mode) {
    case DetailPageMode.DESIGNER:
      return data.designer?.splitPrompts?.length ? DetailPageStage.CONSUME : DetailPageStage.DESIGNING;
    case DetailPageMode.PLANNER:
      return data.planner?.isPlannerComplete ? DetailPageStage.PLANNING : DetailPageStage.CONFIG;
    case DetailPageMode.ENTRY:
    default:
      return DetailPageStage.CONFIG;
  }
}

/** 架构表区块数：后端产出 Markdown 管道表格（数据行形如 `| 1 | S | ... |`，首列为序号），
 * 旧数据兜底兼容编号列表形态（`1. xxx` 行）；表头/分隔行不含纯数字首列，天然不计入 */
export function countArchitectureBlocks(markdown: string | undefined): number {
  return (markdown || '')
    .split('\n')
    .filter((line) => /^\|\s*\d+\s*\|/.test(line.trim()) || /^\d+\./.test(line.trim())).length;
}

/** 纯函数：阶段推进顺序 CONFIG→PLANNING→DESIGNING→CONSUME（终态保持） */
export function nextStageOf(stage: DetailPageStage): DetailPageStage {
  switch (stage) {
    case DetailPageStage.CONFIG:
      return DetailPageStage.PLANNING;
    case DetailPageStage.PLANNING:
      return DetailPageStage.DESIGNING;
    case DetailPageStage.DESIGNING:
    default:
      return DetailPageStage.CONSUME;
  }
}

export interface UseDetailPageStateReturn {
  stage: DetailPageStage;
  planner: DetailPagePlannerData | undefined;
  designer: DetailPageDesignerData | undefined;
  generatedBlocks: number[];
  canGenerateSplitPrompt: boolean;
  setStage: (stage: DetailPageStage) => void;
  updatePlanner: (updates: Partial<DetailPagePlannerData>) => void;
  updateDesigner: (updates: Partial<DetailPageDesignerData>) => void;
  setPlannerComplete: (result: string) => void;
  markBlockGenerated: (index: number) => void;
}

export function useDetailPageState(
  data: AppNode,
  onNodeUpdate: (id: string, data: Partial<AppNode['data']>) => void
): UseDetailPageStateReturn {
  const nodeData = data.data as DetailPageData;
  // 存量旧布局迁移取详情页节点实测高度用（节点组件渲染在 ReactFlow 内，上下文可用）
  const reactFlowInstance = useReactFlow();
  const stage = useMemo(() => deriveStage(nodeData), [nodeData]);
  const planner = nodeData.planner;
  const designer = nodeData.designer;
  const generatedBlocks = useMemo(() => nodeData.generatedBlocks ?? [], [nodeData.generatedBlocks]);

  const setStage = useCallback(
    (nextStage: DetailPageStage) => {
      const updates: Partial<DetailPageData> = { stage: nextStage };
      if (!planner) updates.planner = defaultPlannerData();
      if (!designer) updates.designer = defaultDesignerData();
      onNodeUpdate(data.id, updates);
    },
    [data.id, planner, designer, onNodeUpdate]
  );

  const updatePlanner = useCallback(
    (updates: Partial<DetailPagePlannerData>) => {
      const current = planner ?? defaultPlannerData();
      onNodeUpdate(data.id, {
        planner: { ...current, ...updates },
      });
    },
    [data.id, planner, onNodeUpdate]
  );

  const updateDesigner = useCallback(
    (updates: Partial<DetailPageDesignerData>) => {
      const current = designer ?? defaultDesignerData();
      onNodeUpdate(data.id, {
        designer: { ...current, ...updates },
      });
    },
    [data.id, designer, onNodeUpdate]
  );

  const markBlockGenerated = useCallback(
    (index: number) => {
      const current = generatedBlocks;
      if (current.includes(index)) return;
      // 自动副作用：直接写 store，不记撤销历史
      useNodeStore.getState().updateNode(data.id, {
        generatedBlocks: [...current, index],
      });
    },
    [data.id, generatedBlocks]
  );

  // 监听产品图/竞品图/产品卖点节点被手动删除，实时同步 planner 的节点 ID 列表与数量
  // 优化：仅在节点数量减少时执行检查，避免拖动等无关操作触发回调
  useEffect(() => {
    const productIds = planner?.productImageNodeIds ?? [];
    const competitorIds = planner?.competitorImageNodeIds ?? [];
    const sellingPointIds = planner?.sellingPointNodeIds ?? [];
    if (productIds.length === 0 && competitorIds.length === 0 && sellingPointIds.length === 0) return;

    let lastNodeCount = useNodeStore.getState().nodeMap.size;

    const checkAndSync = () => {
      const state = useNodeStore.getState();
      // 仅在节点数量减少时检查（删除场景），跳过增/改操作
      if (state.nodeMap.size >= lastNodeCount) {
        lastNodeCount = state.nodeMap.size;
        return;
      }
      lastNodeCount = state.nodeMap.size;

      const existingProductIds = productIds.filter((id) => state.nodeMap.has(id));
      const existingCompetitorIds = competitorIds.filter((id) => state.nodeMap.has(id));
      const existingSellingPointIds = sellingPointIds.filter((id) => state.nodeMap.has(id));

      const productChanged = existingProductIds.length !== productIds.length;
      const competitorChanged = existingCompetitorIds.length !== competitorIds.length;
      const sellingPointChanged = existingSellingPointIds.length !== sellingPointIds.length;

      if (!productChanged && !competitorChanged && !sellingPointChanged) return;

      const currentNode = state.nodeMap.get(data.id);
      if (!currentNode) return;
      const currentPlanner = (currentNode.data as DetailPageData).planner;
      if (!currentPlanner) return;

      const updates: Partial<DetailPagePlannerData> = {};
      if (productChanged) {
        updates.productImageNodeIds = existingProductIds;
        updates.productImageCount = existingProductIds.length;
      }
      if (competitorChanged) {
        updates.competitorImageNodeIds = existingCompetitorIds;
        updates.competitorImageCount = existingCompetitorIds.length;
        // 全部竞品图被删除时，不自动切换到 skipCompetitor，保持未选中状态
        if (existingCompetitorIds.length === 0) {
          updates.skipCompetitor = false;
        }
      }
      if (sellingPointChanged) {
        updates.sellingPointNodeIds = existingSellingPointIds;
        // 卖点节点被删除时，切回智能创建模式
        if (existingSellingPointIds.length === 0) {
          updates.sellingPoints = [];
        }
      }

      // 自动副作用：节点删除后自动同步 planner 引用列表，直接写 store，不记历史
      // 删除操作本身已通过 handleNodeDelete 记录历史，此处无需重复记录
      useNodeStore.getState().updateNode(data.id, {
        planner: { ...currentPlanner, ...updates },
      });
    };

    checkAndSync();
    return useNodeStore.subscribe(checkAndSync);
  }, [data.id, planner?.productImageNodeIds, planner?.competitorImageNodeIds, planner?.sellingPointNodeIds, onNodeUpdate]);

  // 监听登记一致性（自愈）：出图节点删除/断线 → 回退登记；存量缺映射 → 从下游连线标题反查补录；
  // 登记被整体误清/漏记（如节点消失竞态窗口内误回退并持久化）→ 从下游连线反查已出图/生成中的
  // 分块节点补录恢复（不设 generated.length===0 短路，空登记也执行补录扫描）。
  // 恒订阅实时读 store，不依赖闭包 designer：存量数据映射为空时旧 guard 会永久短路（监听从未建立），
  // 且 memo 未比较 generatedBlockNodeIds，写入映射也不会重建 effect；挂载时立即自愈一次兜底存量。
  // 分块标题 = formatBlockLabel 前缀（「第N区块」），此处内联避免反向依赖 useDetailPageActions 循环导入。
  // 判定延迟到宏任务：撤销/恢复流程先 setNodes 后 setConnections 两次同步 set，
  // 同步判定会基于中间态（节点已就位、连线尚未恢复）误回退，且映射删除后不可恢复
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const syncRegistry = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        const state = useNodeStore.getState();
        const currentNode = state.nodeMap.get(data.id);
        if (!currentNode) return;
        const currentData = currentNode.data as DetailPageData;
        // 可变副本：校验回退直接移除、漏登记补录直接追加，写回处无需再过滤
        const generated = [...(currentData.generatedBlocks ?? [])];
        const blockNodeIds = { ...(currentData.designer?.generatedBlockNodeIds ?? {}) };
        // 下游出图节点 = 详情页 → 出图节点方向的正向连线；出图节点自身的下游连线不受影响
        const downstream = state.connections
          .filter((c) => c.from === data.id)
          .map((c) => state.nodeMap.get(c.to))
          .filter((n): n is AppNode => !!n);
        const usedIds = new Set(Object.values(blockNodeIds));
        let changed = false;
        const patch: Record<string, string> = {};
        for (let i = generated.length - 1; i >= 0; i--) {
          const index = generated[i];
          const mapped = blockNodeIds[String(index)];
          if (mapped) {
            // 有映射：出图节点已删除，或与详情页的正向连线已断开（视为放弃该分块），回退登记与映射
            const alive = state.nodeMap.has(mapped)
              && state.connections.some((c) => c.from === data.id && c.to === mapped);
            if (!alive) {
              generated.splice(i, 1);
              delete blockNodeIds[String(index)];
              changed = true;
            }
          } else {
            // 存量缺映射：按「第N区块」标题前缀反查下游节点补录；找不到 = 已删/已断线 → 回退登记
            const label = formatBlockLabelOf(index);
            const hit = downstream.find((n) => !usedIds.has(n.id) && (n.title ?? '').startsWith(label));
            if (hit) {
              patch[String(index)] = hit.id;
              usedIds.add(hit.id);
              changed = true;
            } else {
              generated.splice(i, 1);
              changed = true;
            }
          }
        }
        // 漏登记补录：登记曾被整体误清（如节点消失竞态窗口内自愈误回退并持久化）或漏记时，
        // 从下游连线反查「第N区块」标题且已出图（有图）或生成中/排队中的节点，恢复登记与映射；
        // 失败/空节点不补，避免把放弃的分块重新算回「已出图」计数
        for (const n of downstream) {
          if (usedIds.has(n.id)) continue;
          const idx = parseBlockIndexFromTitle(n.title);
          if (idx === undefined || generated.includes(idx)) continue;
          if (!blockNodeActiveOrDone(n)) continue;
          generated.push(idx);
          patch[String(idx)] = n.id;
          usedIds.add(n.id);
          changed = true;
        }
        if (changed) {
          // 自动副作用：登记一致性自愈直接写 store，不记撤销历史（删除/断线操作本身已记录）
          useNodeStore.getState().updateNode(data.id, {
            generatedBlocks: generated,
            designer: { ...(currentData.designer ?? defaultDesignerData()), generatedBlockNodeIds: { ...blockNodeIds, ...patch } },
          });
        }

        // 存量旧布局一次性迁移：v4 之前（660px 固定行距单列重叠 / 两列网格行高自适应致行距大大小小 / 两列奇偶交错并排）
        // 统一重排为竖排堆叠、每竖排 4 个按序填充、满 4 横向开新竖排（各竖排内部等距竖排，整组垂直居中）。
        // 触发 = 已登记分块节点存在且 designer.blockLayoutVersion 低于当前版本；重排后写回版本号，
        // 之后用户手动拖散（版本已是当前值）不再触发。与登记自愈同源惯例：直接写 store 不记撤销历史
        const finalIds = { ...blockNodeIds, ...patch };
        const blockNodes = generated
          .map((idx) => ({ idx, node: state.nodeMap.get(finalIds[String(idx)]) }))
          .filter((b): b is { idx: number; node: AppNode } => !!b.node);
        if (blockNodes.length >= 1 && (currentData.designer?.blockLayoutVersion ?? 0) < BLOCK_LAYOUT_VERSION) {
          const promptMap = new Map((currentData.designer?.splitPrompts ?? []).map((b) => [b.index, b]));
          const footprintOf = (idx: number) => blockNodeFootprintHeight(promptMap.get(idx)?.unitType ?? 'S');
          const colOf = (idx: number) => Math.floor((idx - 1) / BLOCK_STACK_PER_COLUMN);
          // 排高按全部分块预算（分块记录缺失时退化为已登记节点），与创建时定位口径一致避免反复跳动
          const plannedIdx = (currentData.designer?.splitPrompts ?? []).map((b) => b.index);
          const totalIdx = plannedIdx.length > 0 ? plannedIdx : blockNodes.map((b) => b.idx);
          const colCount = Math.max(...totalIdx.map(colOf)) + 1;
          const colSpan = Array.from({ length: colCount }, (_, c) => {
            const colIdx = totalIdx.filter((i) => colOf(i) === c);
            return colIdx.reduce((sum, i) => sum + footprintOf(i), 0)
              + Math.max(colIdx.length - 1, 0) * BLOCK_STACK_GAP_Y;
          });
          const detailHeight = reactFlowInstance.getInternalNode(data.id)?.measured?.height ?? DETAIL_PAGE_HEIGHT_FALLBACK;
          const groupTop = currentNode.y + detailHeight / 2 - Math.max(...colSpan) / 2;
          const slots = new Map<string, { x: number; y: number }>();
          // 排内 y = 排顶 + 前序同排节点占位高度累加 + 前序同排节点数 × 统一间隙：卡片高矮不一排内行距仍处处相等
          for (const { idx } of blockNodes) {
            const c = colOf(idx);
            const preceding = totalIdx.filter((i) => colOf(i) === c && i < idx);
            slots.set(finalIds[String(idx)], {
              x: currentNode.x + 1000 + c * (BLOCK_NODE_SHORT_EDGE + BLOCK_STACK_GAP_X),
              y: groupTop
                + preceding.reduce((sum, i) => sum + footprintOf(i), 0)
                + preceding.length * BLOCK_STACK_GAP_Y,
            });
          }
          // 重读最新快照写位置：上方 changed 分支 updateNode 已产生新 state，闭包旧 state.nodes 会回滚该写入
          const latest = useNodeStore.getState();
          latest.setNodes(
            latest.nodes.map((n) => {
              const p = slots.get(n.id);
              return p ? { ...n, x: p.x, y: p.y } : n;
            }),
          );
          // 版本号与登记映射一并写回：下次同步版本达标不再迁移（沿用 changed 分支刚写入的 finalIds 防回退）
          latest.updateNode(data.id, {
            designer: {
              ...(currentData.designer ?? defaultDesignerData()),
              generatedBlockNodeIds: finalIds,
              blockLayoutVersion: BLOCK_LAYOUT_VERSION,
            },
          });
        }
      });
    };
    const unsubscribe = useNodeStore.subscribe(syncRegistry);
    syncRegistry();
    return () => {
      if (timer) clearTimeout(timer);
      unsubscribe();
    };
  }, [data.id]);

  // 监听手动连线变化，派生 connected* 引用列表：连入「产品图/竞品图」手柄且不属于面板管理数组的源节点
  // 派生时排除面板数组内 source，面板路径与手动路径天然互斥；面板增删后连线归属自动归位
  useEffect(() => {
    const sameIds = (a: string[], b: string[]) =>
      a.length === b.length && a.every((id) => b.includes(id));
    const derive = () => {
      const state = useNodeStore.getState();
      const currentNode = state.nodeMap.get(data.id);
      if (!currentNode) return;
      // planner 未初始化（待配置态）时用默认形状兜底：用户可能先手动拖线后配置面板，
      // 若此处早退，planner 创建前所有手动连线都不会被统计（指示语/面板/执行口径全部漏接）；
      // 无手动连线时派生结果与默认值相同，sameIds 早退不会无谓创建 planner
      const currentPlanner =
        (currentNode.data as DetailPageData).planner ?? defaultPlannerData();

      const panelProductIds = new Set(currentPlanner.productImageNodeIds ?? []);
      const panelCompetitorIds = new Set(currentPlanner.competitorImageNodeIds ?? []);
      const connectedProduct: string[] = [];
      const connectedCompetitor: string[] = [];
      for (const c of state.connections) {
        if (c.to !== data.id || !c.targetHandle) continue;
        if (c.targetHandle.endsWith('-product')) {
          if (!panelProductIds.has(c.from) && !connectedProduct.includes(c.from)) {
            connectedProduct.push(c.from);
          }
        } else if (c.targetHandle.endsWith('-competitor')) {
          if (!panelCompetitorIds.has(c.from) && !connectedCompetitor.includes(c.from)) {
            connectedCompetitor.push(c.from);
          }
        }
      }

      const prevProduct = currentPlanner.connectedProductImageNodeIds ?? [];
      const prevCompetitor = currentPlanner.connectedCompetitorImageNodeIds ?? [];
      if (sameIds(prevProduct, connectedProduct) && sameIds(prevCompetitor, connectedCompetitor)) return;

      // 自动副作用：连线派生直接写 store，不记撤销历史
      // 手动接入竞品图 = 明确参与竞品分析，自动解除历史「跳过」标志，保持面板/指示器/执行口径一致
      const clearSkip = connectedCompetitor.length > 0 && currentPlanner.skipCompetitor;
      useNodeStore.getState().updateNode(data.id, {
        planner: {
          ...currentPlanner,
          connectedProductImageNodeIds: connectedProduct,
          connectedCompetitorImageNodeIds: connectedCompetitor,
          ...(clearSkip ? { skipCompetitor: false } : {}),
        },
      });
    };

    derive();
    return useNodeStore.subscribe(derive);
  }, [data.id]);

  const setPlannerComplete = useCallback(
    (architectureTable: string) => {
      const current = planner ?? defaultPlannerData();
      onNodeUpdate(data.id, {
        planner: {
          ...current,
          architectureTable,
          hasArchitectureTable: true,
          isPlannerComplete: true,
        },
      });
    },
    [data.id, planner, onNodeUpdate]
  );

  const canGenerateSplitPrompt = useMemo(() => {
    return !!designer?.splitPrompts?.length;
  }, [designer]);

  return {
    stage,
    planner,
    designer,
    generatedBlocks,
    canGenerateSplitPrompt,
    setStage,
    updatePlanner,
    updateDesigner,
    setPlannerComplete,
    markBlockGenerated,
  };
}
